// ─── Types d'entrée ───────────────────────────────────────────────────────────

/** Item de niveau 2 (ex : contrat, membre d'un groupe). */
export type DndChild = {
  id: number;
  primaryLabel: string;
  secondaryLabel?: string;
};

/**
 * Item de niveau 1 (ex : client, groupe).
 * Si `children` est vide → mode flat (pas d'accordion).
 */
export type DndParent = {
  id: number;
  label: string;
  children: DndChild[];
  /** Nombre total d'enfants connu à l'avance (optimise isFullParent sans recompter). */
  childrenLength?: number;
};

// ─── Types internes (colonnes) ────────────────────────────────────────────────

/** DndParent enrichi d'un flag indiquant si tous les enfants sont présents dans la colonne. */
export type DndParentEntry = DndParent & { isFullParent: boolean };

export type DndColumn = {
  id: string;
  title: string;
  items: DndParentEntry[];
};

// ─── IDs de colonnes standard ─────────────────────────────────────────────────

export const COL_SOURCE = 'source' as const;
export const COL_TARGET = 'target' as const;
export const COL_EXCLUDED = 'excluded' as const;

// ─── Config générique ─────────────────────────────────────────────────────────

/** Décrit comment extraire les champs DnD depuis un type domaine T. */
export type DndItemMapping<T> = {
  getId: (item: T) => number;
  getLabel: (item: T) => string;
  getChildren?: (item: T) => DndChild[];
  getChildrenLength?: (item: T) => number | undefined;
};

/** Contexte passé à getIsDropDisabled pour évaluer les règles métier. */
export type DropDisabledContext = {
  activeParentId?: number;
  /** columnId → nombre d'items dans la colonne. */
  columnItemCounts: Record<string, number>;
  /** columnId → IDs des parents dans la colonne. */
  columnItemIds: Record<string, number[]>;
};

export type DndColumnSetup = {
  /** 1 = colonne cible seule (consultation), 2 = source + cible, 3 = cible + source + exclus. */
  numColumns: 1 | 2 | 3;
  titles?: { source?: string; target?: string; excluded?: string };
  /**
   * Message affiché quand une colonne est vide.
   * Peut être une chaîne statique ou une fonction recevant les compteurs courants.
   */
  emptyMessages?: Partial<
    Record<
      string,
      string | ((ctx: { columnItemCounts: Record<string, number> }) => string | undefined)
    >
  >;
  getIsDropDisabled?: (columnId: string, ctx: DropDisabledContext) => boolean;
};

export type DndInitialState = {
  targetParents?: number[];
  excludedParents?: number[];
  targetChildren?: number[];
  excludedChildren?: number[];
};

export type DndToolbarConfig = {
  title?: string;
  resetTooltip?: string;
  closeAllTooltip?: string;
  deselectTooltip?: string;
};

export type DndSelectorConfig<T> = {
  data: T[];
  itemConfig: DndItemMapping<T>;
  columnConfig: DndColumnSetup;
  initial?: DndInitialState;
  toolbarConfig?: DndToolbarConfig;
};

// ─── Props du composant générique ─────────────────────────────────────────────

export type DndSelectorProps<T> = {
  config: DndSelectorConfig<T>;
  /** Inutile en lecture seule : aucune modification n'est possible. */
  onChange?: (output: DndSelectorOutput) => void;
  isLoading?: boolean;
  /**
   * Mode consultation : ni drag, ni sélection, ni reset.
   * Les accordéons et la recherche par colonne restent actifs.
   */
  readOnly?: boolean;
  /** Hauteur de la zone de colonnes, en px (défaut 600). */
  height?: number;
};

// ─── Output onChange ──────────────────────────────────────────────────────────

export type DndSelectorOutput = {
  byColumn: Record<
    string,
    {
      /** IDs des parents entièrement dans la colonne (isFullParent = true). */
      fullParentIds: number[];
      /** IDs des enfants appartenant à des parents partiels. */
      partialChildIds: number[];
    }
  >;
};
